import React, { useEffect, useMemo, useState } from "react";
import { Modal, ModalBody } from "reactstrap";

import crossIcon from "../../assets/img/icons/cross_black.svg";
import tickSquareIcon from "../../assets/img/upgrade-plan/tick-square.svg";
import arrowRightIcon from "../../assets/img/upgrade-plan/arrow-right.svg";
import discountBadgeShape from "../../assets/img/upgrade-plan/discount-badge.svg";
import { paddleCheckout } from "./Paddle";
import {
  getCurrencySymbol,
  getPlanName,
  getPlanType,
} from "../../utils/helpers";
import { getPaddleBillingConfig } from "../../utils/paddleBilling";
import { createNotification } from "../../utils/notificationManager";
import {
  getBillingPeriodFromPlanName,
  getBillingPlanTypeFromName,
  getBillingTierLabel,
  getIntegerPriceLabel,
} from "./billingCatalog";
import { classicPlanTemplates, getClassicPlanId } from "./classicPlanCatalog";

const PLAN_PROVIDER = {
  CLASSIC: "classic",
  BILLING: "billing",
};

function buildFeatures(groupLimit) {
  return [
    `Add up to ${groupLimit} Facebook groups`,
    "Unlimited members approval and lead collection",
    "Access to Google Sheet integration",
    "Integration with popular email marketing autoresponders",
    "Lifetime update to software",
    "Option to export data both in CSV and Excel format from dashboard.",
    "Create FB custom and lookalike audience",
    "VIP Facebook group for users",
    "Chat support",
    "14 days refund guarantee",
    "Exclusive support through Zoom/Google Meet/Teams",
  ];
}

function getPackageDefaultPriceLabel(item) {
  const price = item?.price;
  return price != null && price !== "" ? getIntegerPriceLabel(`$${price}`) : "";
}

function findPlanTemplateById(planId) {
  const normalizedPlanId = String(planId || "");

  if (!normalizedPlanId) {
    return null;
  }

  for (const billingPeriod of Object.keys(classicPlanTemplates)) {
    for (const template of classicPlanTemplates[billingPeriod]) {
      const classicId = getClassicPlanId(template.envKey);

      if (
        [template.key, classicId]
          .filter(Boolean)
          .some((id) => String(id) === normalizedPlanId)
      ) {
        return template;
      }
    }
  }

  return null;
}

function getCurrentPlanPriceLabel(
  planId,
  unitPrice,
  currency,
  provider = PLAN_PROVIDER.CLASSIC
) {
  if (unitPrice != null && unitPrice !== "") {
    return getIntegerPriceLabel(
      `${currency ? getCurrencySymbol(currency) : "$"}${unitPrice}`
    );
  }

  if (provider === PLAN_PROVIDER.BILLING) {
    return "Price unavailable";
  }

  return getPackageDefaultPriceLabel(findPlanTemplateById(planId));
}

function buildClassicPlanPackages(billingPeriod) {
  return (classicPlanTemplates[billingPeriod] || []).flatMap((template) => {
    const classicId = getClassicPlanId(template.envKey);

    if (!classicId) {
      return [];
    }

    return [
      {
        provider: PLAN_PROVIDER.CLASSIC,
        id: classicId,
        name: template.name,
        tierLabel: template.tierLabel,
        price: template.price,
        groupLimit: template.groupLimit,
        features: buildFeatures(template.groupLimit),
        badge: template.badge,
      },
    ];
  });
}

export function buildBillingPlanPackages(billingPeriod, billingPlans = []) {
  return billingPlans
    .filter(
      (plan) => getBillingPeriodFromPlanName(plan?.plan_name) === billingPeriod
    )
    .map((plan, index) => ({
      provider: PLAN_PROVIDER.BILLING,
      id: plan.price_id,
      name: plan.plan_name,
      tierLabel: getBillingTierLabel(plan.plan_name),
      price: null,
      groupLimit: plan.allowed_groups,
      features: buildFeatures(plan.allowed_groups),
      badge:
        /pro/i.test(getBillingTierLabel(plan.plan_name)) || index === 1
          ? "Most Popular"
          : null,
      unavailableReason: null,
    }));
}

export function buildPlanPackages(
  billingPeriod,
  preferredProvider,
  billingPlans = []
) {
  return preferredProvider === PLAN_PROVIDER.BILLING
    ? buildBillingPlanPackages(billingPeriod, billingPlans)
    : buildClassicPlanPackages(billingPeriod);
}

function UpgradePlanCard({
  item,
  billingPeriod,
  isCurrent,
  isFeatured,
  onChoose,
  isDisabled,
  isPricePreviewLoading,
}) {
  const priceSuffix = billingPeriod === "yearly" ? "/ Year" : "/ Month";
  const priceLabel =
    isPricePreviewLoading && item.provider === PLAN_PROVIDER.BILLING
      ? "Loading price…"
      : item.previewPriceLabel ||
        getPackageDefaultPriceLabel(item) ||
        "Price unavailable";

  const cardInner = (
    <>
      <div className="upgrade-plan-card__top">
        <div className="upgrade-plan-title-pill">{item.tierLabel}</div>
        <div className="upgrade-plan-price-row">
          <span className="upgrade-plan-price">{priceLabel}</span>
          <span className="upgrade-plan-price-suffix">{priceSuffix}</span>
        </div>
      </div>
      <ul className="upgrade-plan-features">
        {item.features.map((text, i) => (
          <li key={i} className="upgrade-plan-feature">
            <img
              src={tickSquareIcon}
              alt=""
              className="upgrade-plan-feature__icon"
              width={24}
              height={24}
            />
            <span>{text}</span>
          </li>
        ))}
      </ul>
      <button
        type="button"
        className="upgrade-plan-cta"
        onClick={onChoose}
        disabled={isCurrent || isDisabled}
      >
        <span className="upgrade-plan-cta__arrow" aria-hidden="true">
          <img src={arrowRightIcon} alt="" width={24} height={24} />
        </span>
        <span className="upgrade-plan-cta__label">
          {isCurrent
            ? "Current plan"
            : isDisabled
            ? "Unavailable"
            : "Choose plan"}
        </span>
      </button>
      {item.unavailableReason && (
        <p
          style={{
            marginTop: "12px",
            fontSize: "13px",
            lineHeight: "1.5",
            color: "#7a5b00",
          }}
        >
          {item.unavailableReason}
        </p>
      )}
    </>
  );

  return (
    <div
      className={`upgrade-plans-grid__cell ${!isFeatured ? "upgrade-plans-grid__cell--offset" : ""}`}
    >
      <div
        className={`upgrade-plan-card-host ${isCurrent ? "upgrade-plan-card-host--current" : ""} ${isFeatured ? "upgrade-plan-card-host--featured" : ""}`}
      >
        {isFeatured ? (
          <div className="upgrade-plan-featured-shell">
            <div className="upgrade-plan-best-deal">Best Deal</div>
            <div className="upgrade-plan-card upgrade-plan-card--featured-inner">
              {cardInner}
            </div>
          </div>
        ) : (
          <div className="upgrade-plan-card upgrade-plan-card--standard">
            {cardInner}
          </div>
        )}
        {isCurrent && (
          <div className="upgrade-plan-current-overlay">
            <span className="upgrade-plan-current-badge">Current Plan</span>
          </div>
        )}
      </div>
    </div>
  );
}

function UpgradePlanModal({
  modal,
  setModal,
  planStatus,
  planId,
  currentPlanUnitPrice,
  currentPlanCurrency,
  currentPlanName,
  userMail,
  isBilling,
  billingPlans,
  billingPlansLoading,
  billingPlansError,
  billingPriceLabels,
  billingPricePreviewLoading,
  retryBillingPlans,
  billingActions,
  initiatePlanUpgrade,
  initiateBillingCheckout,
  billingPlanChangeLoading,
  billingMutationError,
  confirmBillingPlanChange,
}) {
  const [billingPeriod, setBillingPeriod] = useState("yearly");
  const [selectedBillingPlan, setSelectedBillingPlan] = useState(null);

  const openCheckout = (id, name) => {
    paddleCheckout(id, name, userMail);
  };

  useEffect(() => {
    if (planId) {
      const planType = currentPlanName
        ? getBillingPeriodFromPlanName(currentPlanName)
        : getPlanType(planId);
      if (planType.includes("month")) {
        setBillingPeriod("monthly");
      } else {
        setBillingPeriod("yearly");
      }
    }
  }, [currentPlanName, planId]);

  useEffect(() => {
    if (!modal?.upgradeModal) {
      setSelectedBillingPlan(null);
    }
  }, [modal?.upgradeModal]);

  const hasActivePlan =
    planStatus && planStatus !== "canceled" && planStatus !== "inactive";
  const subscriptionActions = billingActions || {};
  const billingConfig = getPaddleBillingConfig();
  const preferredProvider =
    !hasActivePlan || isBilling ? PLAN_PROVIDER.BILLING : PLAN_PROVIDER.CLASSIC;
  const availableBillingPeriods = useMemo(
    () =>
      [...new Set(
        (billingPlans || [])
          .map((plan) => getBillingPeriodFromPlanName(plan?.plan_name))
          .filter(Boolean)
      )],
    [billingPlans]
  );
  const packages = useMemo(
    () => buildPlanPackages(billingPeriod, preferredProvider, billingPlans),
    [billingPeriod, billingPlans, preferredProvider]
  );
  const packagesWithPreviewPrices = useMemo(
    () =>
      packages.map((item) => ({
        ...item,
        previewPriceLabel: billingPriceLabels?.[item.id] || null,
      })),
    [billingPriceLabels, packages]
  );
  const showBillingConfigError =
    !hasActivePlan &&
    preferredProvider === PLAN_PROVIDER.BILLING &&
    !billingConfig.isBillingConfigured;
  const showBillingCatalogLoading =
    preferredProvider === PLAN_PROVIDER.BILLING && billingPlansLoading;
  const showBillingCatalogError =
    preferredProvider === PLAN_PROVIDER.BILLING && Boolean(billingPlansError);
  const showBillingCatalogEmpty =
    preferredProvider === PLAN_PROVIDER.BILLING &&
    !billingPlansLoading &&
    !billingPlansError &&
    availableBillingPeriods.length === 0;
  const showBillingPreviewStep =
    Boolean(selectedBillingPlan) &&
    !showBillingCatalogLoading &&
    !showBillingCatalogError &&
    !showBillingCatalogEmpty;

  useEffect(() => {
    if (preferredProvider !== PLAN_PROVIDER.BILLING || !availableBillingPeriods.length) {
      return;
    }

    const currentPlanPeriod = getBillingPeriodFromPlanName(currentPlanName);
    const preferredPeriod =
      currentPlanPeriod && availableBillingPeriods.includes(currentPlanPeriod)
        ? currentPlanPeriod
        : availableBillingPeriods.includes("yearly")
        ? "yearly"
        : availableBillingPeriods[0];

    if (!availableBillingPeriods.includes(billingPeriod)) {
      setBillingPeriod(preferredPeriod);
    }
  }, [
    availableBillingPeriods,
    billingPeriod,
    currentPlanName,
    preferredProvider,
  ]);

  const toggle = () => {
    setSelectedBillingPlan(null);
    setModal({
      upgradeModal: !modal.upgradeModal,
    });
  };

  const handleChoosePlan = (item) => {
    if (item.unavailableReason) {
      createNotification("warning", item.unavailableReason, 4000);
      return;
    }

    if (String(item.id) === String(planId) && planStatus !== "canceled") {
      createNotification("warning", "You already have this plan", 2000);
      return;
    }

    if (item.provider === PLAN_PROVIDER.BILLING && !billingConfig.isBillingConfigured) {
      createNotification("warning", billingConfig.configError, 4000);
      return;
    }

    if (
      item.provider === PLAN_PROVIDER.BILLING &&
      !(billingPlans || []).some((plan) => plan?.price_id === item.id)
    ) {
      createNotification(
        "warning",
        "This Billing plan is no longer available. Reload the catalog and try again.",
        4000
      );
      return;
    }

    if (!hasActivePlan && item.provider === PLAN_PROVIDER.CLASSIC) {
      openCheckout(item.id, item.name);
      toggle();
      return;
    }

    if (!hasActivePlan && item.provider === PLAN_PROVIDER.BILLING) {
      initiateBillingCheckout({
        price_id: item.id,
        email: userMail,
      });
      toggle();
      return;
    }

    if (item.provider === PLAN_PROVIDER.CLASSIC && !isBilling) {
      initiatePlanUpgrade({
        plan_id: item.id,
      });
      toggle();
      return;
    }

    if (item.provider === PLAN_PROVIDER.BILLING && isBilling) {
      setSelectedBillingPlan(item);
      return;
    }

    createNotification("warning", "Unable to change the selected plan.", 3000);
  };

  const isCurrentPlan = (item) =>
    String(item.id) === String(planId) && planStatus !== "canceled";

  const displayedCurrentPlanName = currentPlanName || getPlanName(planId);
  const currentPlanType = currentPlanName
    ? getBillingPlanTypeFromName(currentPlanName)
    : getPlanType(planId);
  const currentPlanPrice = getCurrentPlanPriceLabel(
    planId,
    currentPlanUnitPrice,
    currentPlanCurrency,
    isBilling ? PLAN_PROVIDER.BILLING : PLAN_PROVIDER.CLASSIC
  );
  const targetPlanName = selectedBillingPlan?.name || "Unknown";
  const targetPlanType = getBillingPlanTypeFromName(targetPlanName);
  const targetPlanPrice = billingPricePreviewLoading
    ? "Loading price…"
    : selectedBillingPlan?.previewPriceLabel || "Price unavailable";

  return (
    <div>
      <Modal
        isOpen={modal.upgradeModal}
        toggle={toggle}
        backdrop={true}
        keyboard={true}
        centered={true}
        size="xl"
        className="upgrade-modal"
      >
        <button
          type="button"
          className="cross-icon upgrade-modal__close"
          onClick={toggle}
          aria-label="Close"
        >
          <img src={crossIcon} alt="" width={18} height={18} />
        </button>

        <ModalBody>
          <div className="upgrade-modal-body">
            <div className="upgrade-header">
              <h3>Choose the plan that fits your growth</h3>
              <p>Simple pricing. Powerful results. Cancel anytime.</p>
            </div>

            {showBillingConfigError && (
              <div
                style={{
                  marginBottom: "20px",
                  padding: "14px 16px",
                  borderRadius: "12px",
                  backgroundColor: "#fff3cd",
                  color: "#7a5b00",
                  fontSize: "14px",
                  lineHeight: "1.5",
                }}
              >
                Paddle Billing sandbox is not configured correctly for this
                frontend. {billingConfig.configError}
              </div>
            )}

            {preferredProvider === PLAN_PROVIDER.BILLING &&
              billingPlansLoading && (
                <div
                  style={{
                    marginBottom: "20px",
                    padding: "14px 16px",
                    borderRadius: "12px",
                    backgroundColor: "#eef4ff",
                    color: "#1d4ed8",
                    fontSize: "14px",
                  }}
                >
                  Loading Billing plans…
                </div>
              )}

            {preferredProvider === PLAN_PROVIDER.BILLING &&
              billingPlansError && (
                <div
                  style={{
                    marginBottom: "20px",
                    padding: "14px 16px",
                    borderRadius: "12px",
                    backgroundColor: "#fff3f2",
                    color: "#b42318",
                    fontSize: "14px",
                    lineHeight: "1.5",
                  }}
                >
                  <div>{billingPlansError}</div>
                  <button
                    type="button"
                    onClick={retryBillingPlans}
                    disabled={billingPlansLoading}
                    style={{
                      marginTop: "10px",
                      border: "1px solid currentColor",
                      background: "transparent",
                      color: "inherit",
                      borderRadius: "8px",
                      padding: "8px 12px",
                    }}
                  >
                    Retry
                  </button>
                </div>
              )}

            {subscriptionActions.can_change_plan === false &&
              subscriptionActions.blocked_reason && (
                <div
                  style={{
                    marginBottom: "20px",
                    padding: "14px 16px",
                    borderRadius: "12px",
                    backgroundColor: "#fff3cd",
                    color: "#7a5b00",
                    fontSize: "14px",
                    lineHeight: "1.5",
                  }}
                >
                  {subscriptionActions.blocked_reason}
                </div>
              )}

            {showBillingPreviewStep ? (
              <div
                style={{
                  border: "1px solid #dfe5f3",
                  borderRadius: "20px",
                  padding: "24px",
                  backgroundColor: "#ffffff",
                }}
              >
                <h4
                  style={{
                    fontSize: "24px",
                    marginBottom: "10px",
                    color: "#101828",
                  }}
                >
                  Review your plan change
                </h4>
                {billingMutationError && (
                  <div
                    style={{
                      marginBottom: "20px",
                      padding: "14px 16px",
                      borderRadius: "14px",
                      backgroundColor: "#fff3f2",
                      color: "#b42318",
                      lineHeight: "1.6",
                    }}
                  >
                    {billingMutationError}
                  </div>
                )}
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: "16px",
                    marginBottom: "20px",
                  }}
                >
                  <div
                    style={{
                      padding: "16px",
                      borderRadius: "16px",
                      backgroundColor: "#f8fafc",
                    }}
                  >
                    <p style={{ marginBottom: "8px", color: "#475467" }}>
                      Current plan
                    </p>
                    <strong>{displayedCurrentPlanName}</strong>
                    <p style={{ margin: "8px 0 0", color: "#475467" }}>
                      {currentPlanPrice}
                      {currentPlanType || "Current interval"}
                    </p>
                  </div>
                  <div
                    style={{
                      padding: "16px",
                      borderRadius: "16px",
                      backgroundColor: "#ecfeff",
                    }}
                  >
                    <p style={{ marginBottom: "8px", color: "#0f766e" }}>
                      New plan
                    </p>
                    <strong>{targetPlanName}</strong>
                    <p style={{ margin: "8px 0 0", color: "#0f766e" }}>
                      {targetPlanPrice}
                      {targetPlanType || ""}
                    </p>
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "12px",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedBillingPlan(null);
                    }}
                    disabled={billingPlanChangeLoading}
                    style={{
                      border: "1px solid #d0d5dd",
                      backgroundColor: "#fff",
                      color: "#344054",
                      borderRadius: "12px",
                      padding: "12px 18px",
                    }}
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      confirmBillingPlanChange({
                        new_price_id: selectedBillingPlan?.id,
                        proration_billing_mode: "prorated_immediately",
                        on_payment_failure: "prevent_change",
                      });
                    }}
                    disabled={billingPlanChangeLoading}
                    style={{
                      border: "none",
                      backgroundColor: "#33CDD1",
                      color: "#fff",
                      borderRadius: "12px",
                      padding: "12px 18px",
                    }}
                  >
                    {billingPlanChangeLoading ? "Applying..." : "Confirm change"}
                  </button>
                </div>
              </div>
            ) : (
              <>
                {!showBillingCatalogLoading &&
                  !showBillingCatalogError &&
                  !showBillingCatalogEmpty && (
                <div className="upgrade-plan-billing-row">
                  <div className="upgrade-plan-toggle-wrap">
                    <div className="upgrade-plan-save-badge">
                      <img
                        src={discountBadgeShape}
                        alt=""
                        className="upgrade-plan-save-badge__shape"
                        width={56}
                        height={56}
                      />
                      <div className="upgrade-plan-save-badge__text">
                        <span className="upgrade-plan-save-badge__pct">60%+</span>
                        <span className="upgrade-plan-save-badge__save">Save</span>
                      </div>
                    </div>
                    <div
                      className="upgrade-plan-toggle-pair"
                      role="group"
                      aria-label="Billing period"
                    >
                      {(preferredProvider === PLAN_PROVIDER.CLASSIC ||
                        availableBillingPeriods.includes("yearly")) && (
                      <button
                        type="button"
                        className={`upgrade-plan-toggle-btn upgrade-plan-toggle-btn--yearly ${billingPeriod === "yearly" ? "is-active" : ""}`}
                        onClick={() => setBillingPeriod("yearly")}
                        aria-pressed={billingPeriod === "yearly"}
                      >
                        Yearly
                      </button>
                      )}
                      {(preferredProvider === PLAN_PROVIDER.CLASSIC ||
                        availableBillingPeriods.includes("monthly")) && (
                      <button
                        type="button"
                        className={`upgrade-plan-toggle-btn upgrade-plan-toggle-btn--monthly ${billingPeriod === "monthly" ? "is-active" : ""}`}
                        onClick={() => setBillingPeriod("monthly")}
                        aria-pressed={billingPeriod === "monthly"}
                      >
                        Monthly
                      </button>
                      )}
                    </div>
                  </div>
                </div>
                  )}

                {!showBillingCatalogLoading &&
                  !showBillingCatalogError &&
                  !showBillingCatalogEmpty && (
                  <div className="upgrade-plans-grid">
                    {packagesWithPreviewPrices.map((item, index) => (
                    <UpgradePlanCard
                      key={`${item.id}-${index}`}
                      item={item}
                      billingPeriod={billingPeriod}
                      isCurrent={isCurrentPlan(item)}
                      isFeatured={item.badge === "Most Popular"}
                      isDisabled={
                        Boolean(item.unavailableReason) ||
                        (item.provider === PLAN_PROVIDER.BILLING &&
                          isBilling &&
                          hasActivePlan &&
                          subscriptionActions.can_change_plan === false)
                      }
                      isPricePreviewLoading={billingPricePreviewLoading}
                      onChoose={() => handleChoosePlan(item)}
                    />
                    ))}
                  </div>
                )}
              </>
            )}

            <div className="upgrade-footer-hint">
              <span>
                14-day money-back guarantee • No hidden fees • Secure checkout
              </span>
            </div>
          </div>
        </ModalBody>
      </Modal>
    </div>
  );
}

export default UpgradePlanModal;
